// PaymentForm — payment capture for marketplace checkout.
//
// Implements the payment-capture design's §3.2-§3.8 and §4.5. Three things
// about it are load-bearing and easy to undo by accident:
//
// 1. THE METHOD IS DERIVED, NOT CHOSEN (§3.2, D1). The billing country decides:
//    US means ACH, anywhere else means credit card. There is no tab strip, no
//    picker and no prop that lets a caller ask for the other form. The tabs
//    this file used to render were REMOVED rather than disabled, because a
//    disabled tab advertises an option the customer cannot have and invites a
//    support call. The form states the method plainly with a short reason
//    instead, since an unexplained absence of choice reads as a bug.
//
//    The client is not the guard. The checkout service derives the same method
//    from the country snapshotted onto the order and rejects a mismatch (§1.3,
//    §3.2). This copy exists so the customer is never shown a form that cannot
//    succeed.
//
// 2. NOTHING IS PROCESSED HERE (§0). Format validation is local arithmetic
//    (payment-format.ts); capture posts to our own service, which encrypts and
//    records (payment-instrument-api.ts). No gateway, no bank, no card network,
//    no tokenization SDK. The previous `devTokenize()` placeholder — a
//    timestamp and the last four digits, submitted as the processor reference
//    while the real details were discarded (finding F1) — is gone, along with
//    the swap-point comment pointing at Aldelo Pay, which was decommissioned
//    under TD-373 (F11).
//
// 3. NO CVV FIELD, DELIBERATELY (§3.4). A CVV cannot be retained past
//    authorization and this system authorizes nothing, so collecting one would
//    ask the customer for a secret with nothing to spend it on. Its absence is
//    a decision, not an omission.
//
// The raw PAN and account number still never leave this component: they go to
// the capture call and die with the unmount. What travels upward is the
// server's reference plus display-safe values, which is why the checkout body
// and the review panel are unchanged in shape.
//
// Inline styles only — no Tailwind. Themed via useEmbedTokens(); composes
// embed-shared primitives (EmbedField/EmbedInput/EmbedSelect/EmbedButton).

import { useCallback, useMemo, useState } from "react";
import {
  useEmbedTokens,
  EmbedField,
  EmbedInput,
  EmbedButton,
  EmbedBanner,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import {
  derivePaymentMethod,
  paymentMethodLabel,
  paymentMethodReason,
  type DerivedPaymentMethod,
} from "./payment-method-geo";
import {
  deriveCardBrand,
  formatCardNumber,
  lastFour,
  parseExpiry,
  validateAccountNumber,
  validateCardNumber,
  validateExpiry,
  validateHolderName,
  validateRoutingNumber,
  type AchAccountType,
  type CardBrand,
} from "./payment-format";
import { capturePaymentInstrument } from "./payment-instrument-api";
import { PaymentTermsGate, termsAreAvailable } from "./PaymentTermsGate";
import type { PaymentTermsDocumentWire } from "./payment-terms-api";

/**
 * What the payment step hands to checkout.
 *
 * `payment_instrument_id` is the server's reference to the recorded
 * instrument. It was renamed from `payment_token` by TD-461: the Go
 * order-create handler REQUIRES `payment_instrument_id` and silently IGNORES
 * the legacy `payment_token` this used to populate, so every submit 422'd —
 * the name now matches the wire it actually travels on.
 *
 * `accepted_terms_ids` is the SET of revision references the customer accepted
 * (§4.4), never a boolean. `accepted_at` stamps when.
 */
export interface PaymentFormData {
  payment_method: DerivedPaymentMethod;
  /** Server-issued reference to the recorded instrument. */
  payment_instrument_id: string;
  /** Display-safe last four, from the capture response. */
  last4: string;
  /** Derived brand, card only. */
  brand?: CardBrand;
  cardName?: string;
  accountName?: string;
  /**
   * F23: always "checking" — the account type is locked to business checking,
   * so this is a display fact the review step reads, never a choice that was
   * made. (The server independently refuses any other type at capture.)
   */
  accountType?: AchAccountType;
  /** Revision references, one per document presented (§4.4). */
  accepted_terms_ids: string[];
  /**
   * When the customer ticked, RFC3339.
   *
   * NOT sent on the order body, deliberately: §4.4 has the SERVER stamp the
   * acceptance timestamp in the same transaction as the order, and a
   * client-supplied time would be an unverifiable claim about a legal event.
   * Kept because the review step and any future "you accepted these terms"
   * affordance need a local answer, and because reading it here is how the
   * next person learns the server owns the recorded one.
   */
  accepted_at: string;
}

export interface PaymentFormProps {
  /**
   * ISO-3166 alpha-2 of the order's BILLING address — the geo authority (§1.1).
   * `null` when no billing address is resolvable, which blocks the form: §1.6
   * is explicit that there is no safe default, because US wrongly enables ACH
   * for an international buyer.
   */
  billingCountryCode: string | null;
  /**
   * Database id of the order's BILLING address row — the geo authority's row
   * itself (§1.1). Sent on the capture wire so the SERVER derives the method
   * from the same row the order will reference. `null` blocks the form like an
   * unresolvable country: an instrument captured without a geo authority is an
   * instrument no method can be derived for (TD-461).
   */
  billingAddressId: number | null;
  /** Active terms revisions; `null` while loading. */
  termsDocuments: PaymentTermsDocumentWire[] | null;
  /** Terms fetch failure message; blocks submission while set (§4.5). */
  termsError: string | null;
  onSubmit: (data: PaymentFormData) => void;
  onBack?: () => void;
  submitting?: boolean;
}

type ErrorMap = Record<string, string>;

export function PaymentForm({
  billingCountryCode,
  billingAddressId,
  termsDocuments,
  termsError,
  onSubmit,
  onBack,
  submitting,
}: PaymentFormProps) {
  const t = useEmbedTokens();
  const method = derivePaymentMethod(billingCountryCode);

  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardName, setCardName] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [errors, setErrors] = useState<ErrorMap>({});
  const [accepted, setAccepted] = useState(false);
  const [acceptanceError, setAcceptanceError] = useState<string | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  const clearError = useCallback((key: string) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  /** Progressive validation: validate on blur, clear on change (§3.8). */
  const validateOnBlur = useCallback((key: string, message: string | null) => {
    setErrors((prev) => {
      if (message === null) {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === message) return prev;
      return { ...prev, [key]: message };
    });
  }, []);

  const derivedBrand = useMemo(() => deriveCardBrand(cardNumber), [cardNumber]);
  const termsReady = termsAreAvailable(termsDocuments, termsError);
  const busy = !!submitting || capturing;
  // §3.8/§4.5: the submit button is disabled until acceptance is ticked. An
  // unticked box is a blocked submit, not a warning banner. It is also disabled
  // when the method cannot be derived or the terms cannot be shown — in both
  // cases there is nothing valid the press could do.
  const canSubmit = method !== null && billingAddressId !== null && termsReady && accepted && !busy;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (method === null || billingAddressId === null) return;

      const errs: ErrorMap = {};
      if (method === "card") {
        const nameErr = validateHolderName(cardName, "Name on card");
        if (nameErr) errs.cardName = nameErr;
        const numberErr = validateCardNumber(cardNumber);
        if (numberErr) errs.cardNumber = numberErr;
        const expiryErr = validateExpiry(cardExpiry, new Date());
        if (expiryErr) errs.cardExpiry = expiryErr;
      } else {
        const nameErr = validateHolderName(accountName, "Account holder name");
        if (nameErr) errs.accountName = nameErr;
        const routingErr = validateRoutingNumber(routingNumber);
        if (routingErr) errs.routingNumber = routingErr;
        const accountErr = validateAccountNumber(accountNumber);
        if (accountErr) errs.accountNumber = accountErr;
      }

      setErrors(errs);

      // Acceptance is a precondition of submission (§4.5). The disabled submit
      // button is convenience, not the guard, and this is the guard: a form can
      // be submitted without its submit button being clicked — implicit
      // submission from a text field, and a programmatic submit — so the rule
      // has to live in the handler or it does not hold. (The server re-checks
      // acceptance too and rejects an order without it; that is the real
      // authority. This is the second of three.)
      //
      // Checked alongside the field errors rather than before them, so a
      // customer with both problems is told about both at once instead of
      // discovering the second after fixing the first.
      const acceptanceProblem = !termsReady
        ? "Payment terms are not available right now, so this order cannot be placed."
        : !accepted
          ? "Please accept the payment terms to continue."
          : null;
      setAcceptanceError(acceptanceProblem);

      if (Object.keys(errs).length > 0 || acceptanceProblem !== null) return;

      // Record the instrument (D6). Multi-step mutation via async/await — never
      // nested callbacks.
      setCapturing(true);
      setCaptureError(null);
      try {
        const captured =
          method === "card"
            ? await (async () => {
                const expiry = parseExpiry(cardExpiry);
                // validateExpiry already proved this parses; the guard exists so
                // the non-null case is a fact rather than an assertion.
                if (!expiry) throw new Error("expiry failed to parse after validation");
                return capturePaymentInstrument({
                  payment_method: "card",
                  billing_address_id: billingAddressId,
                  name_on_card: cardName.trim(),
                  card_number: cardNumber.replace(/[\s-]/g, ""),
                  expiry_month: expiry.month,
                  expiry_year: expiry.year,
                });
              })()
            : await capturePaymentInstrument({
                payment_method: "ach",
                billing_address_id: billingAddressId,
                account_holder_name: accountName.trim(),
                routing_number: routingNumber.replace(/[\s-]/g, ""),
                account_number: accountNumber.replace(/[\s-]/g, ""),
                // F23: business checking only — the wire value is a constant,
                // not a state read, because no control exists that could
                // supply anything else. (The server refuses savings too.)
                account_type: "checking",
              });

        onSubmit({
          payment_method: method,
          payment_instrument_id: captured.id,
          // Prefer the server's derived last4; fall back to the local
          // derivation only so the review step is never blank.
          last4: captured.last4 || lastFour(method === "card" ? cardNumber : accountNumber),
          ...(method === "card"
            ? { brand: captured.brand ?? (derivedBrand === "visa" || derivedBrand === "mastercard" ? derivedBrand : undefined), cardName: cardName.trim() }
            : { accountName: accountName.trim(), accountType: "checking" as AchAccountType }),
          accepted_terms_ids: (termsDocuments ?? []).map((d) => d.id),
          accepted_at: new Date().toISOString(),
        });
      } catch (err) {
        // Deliberate production error-path observability. The MESSAGE only —
        // never the request body, which holds the instrument.
        console.error("[checkout] payment capture failed", err instanceof Error ? err.message : err);
        setCaptureError(
          err instanceof ApiError
            ? err.message
            : "We could not save your payment details. Please try again.",
        );
      } finally {
        setCapturing(false);
      }
    },
    [
      method, billingAddressId, cardName, cardNumber, cardExpiry, accountName,
      routingNumber, accountNumber, accepted, termsReady, termsDocuments,
      derivedBrand, onSubmit,
    ],
  );

  const heading = (
    <h3
      style={{
        fontSize: t.fontSize.lg,
        fontWeight: 600,
        color: t.colors.text,
        margin: `0 0 ${t.spacing.sm}px 0`,
      }}
    >
      Payment method
    </h3>
  );

  // §1.6: no resolvable billing country — or no billing row to derive from —
  // is a rejection, not a default. Rendering either form here would be
  // guessing which rail the customer is on.
  if (method === null || billingAddressId === null) {
    return (
      <div>
        {heading}
        <EmbedBanner tone="error">
          We need a billing address before we can take payment. Please go back and choose one.
        </EmbedBanner>
        {onBack && (
          <div style={{ display: "flex", gap: t.spacing.md, marginTop: t.spacing.lg }}>
            <EmbedButton type="button" variant="secondary" onClick={onBack}>
              Back
            </EmbedButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {heading}

      {/* The method, stated, with a reason — never a dead tab (§3.2). */}
      <div
        style={{
          border: `1px solid ${t.colors.borderSubtle}`,
          borderRadius: t.borderRadius,
          padding: t.spacing.md,
          marginBottom: t.spacing.lg,
          background: t.colors.surfaceSunken,
        }}
      >
        <p style={{ margin: 0, fontSize: t.fontSize.md, fontWeight: 600, color: t.colors.text }}>
          {paymentMethodLabel(method)}
        </p>
        <p style={{ margin: `${t.spacing.xs}px 0 0 0`, fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>
          {paymentMethodReason(method)}
        </p>
      </div>

      {captureError && (
        <div style={{ marginBottom: t.spacing.md }}>
          <EmbedBanner tone="error">{captureError}</EmbedBanner>
        </div>
      )}

      {method === "card" ? (
        <>
          <EmbedField label="Name on card" htmlFor="pay-card-name" required error={errors.cardName}>
            <EmbedInput
              id="pay-card-name"
              type="text"
              value={cardName}
              onChange={(e) => { setCardName(e.target.value); clearError("cardName"); }}
              onBlur={() => validateOnBlur("cardName", validateHolderName(cardName, "Name on card"))}
              aria-required="true"
              invalid={!!errors.cardName}
              aria-invalid={!!errors.cardName}
              autoComplete="cc-name"
            />
          </EmbedField>
          <EmbedField
            label="Card number"
            htmlFor="pay-card-number"
            required
            error={errors.cardNumber}
            help="Visa and Mastercard only."
          >
            <EmbedInput
              id="pay-card-number"
              type="text"
              inputMode="numeric"
              value={cardNumber}
              onChange={(e) => { setCardNumber(formatCardNumber(e.target.value)); clearError("cardNumber"); }}
              onBlur={() => validateOnBlur("cardNumber", validateCardNumber(cardNumber))}
              placeholder="1234 5678 9012 3456"
              aria-required="true"
              invalid={!!errors.cardNumber}
              aria-invalid={!!errors.cardNumber}
              autoComplete="cc-number"
            />
          </EmbedField>
          <div style={{ maxWidth: 200 }}>
            <EmbedField label="Expiry" htmlFor="pay-card-expiry" required error={errors.cardExpiry}>
              <EmbedInput
                id="pay-card-expiry"
                type="text"
                inputMode="numeric"
                value={cardExpiry}
                onChange={(e) => { setCardExpiry(e.target.value); clearError("cardExpiry"); }}
                onBlur={() => validateOnBlur("cardExpiry", validateExpiry(cardExpiry, new Date()))}
                placeholder="MM/YY"
                aria-required="true"
                invalid={!!errors.cardExpiry}
                aria-invalid={!!errors.cardExpiry}
                autoComplete="cc-exp"
              />
            </EmbedField>
          </div>
          {/* No CVC field — §3.4. Do not add one without answering OQ-3. */}
        </>
      ) : (
        <>
          <EmbedField label="Account holder name" htmlFor="pay-ach-name" required error={errors.accountName}>
            <EmbedInput
              id="pay-ach-name"
              type="text"
              value={accountName}
              onChange={(e) => { setAccountName(e.target.value); clearError("accountName"); }}
              onBlur={() => validateOnBlur("accountName", validateHolderName(accountName, "Account holder name"))}
              aria-required="true"
              invalid={!!errors.accountName}
              aria-invalid={!!errors.accountName}
              autoComplete="name"
            />
          </EmbedField>
          {/* §3.8 asks for "the ACH equivalents" of the card autofill hints.
              There are none: the HTML autofill spec defines cc-name / cc-number
              / cc-exp but NO token for a routing or account number, and
              inventing one would emit a value every browser ignores while
              reading as though autofill were wired. `off` is the honest
              setting, and the holder-name field takes the real `name` token so
              the one part that CAN autofill does. */}
          <EmbedField
            label="Routing number"
            htmlFor="pay-ach-routing"
            required
            error={errors.routingNumber}
            help="The 9-digit number on the bottom left of a check."
          >
            <EmbedInput
              id="pay-ach-routing"
              type="text"
              inputMode="numeric"
              value={routingNumber}
              onChange={(e) => { setRoutingNumber(e.target.value); clearError("routingNumber"); }}
              onBlur={() => validateOnBlur("routingNumber", validateRoutingNumber(routingNumber))}
              aria-required="true"
              invalid={!!errors.routingNumber}
              aria-invalid={!!errors.routingNumber}
              autoComplete="off"
            />
          </EmbedField>
          <EmbedField label="Account number" htmlFor="pay-ach-account" required error={errors.accountNumber}>
            <EmbedInput
              id="pay-ach-account"
              type="text"
              inputMode="numeric"
              value={accountNumber}
              onChange={(e) => { setAccountNumber(e.target.value); clearError("accountNumber"); }}
              onBlur={() => validateOnBlur("accountNumber", validateAccountNumber(accountNumber))}
              aria-required="true"
              invalid={!!errors.accountNumber}
              aria-invalid={!!errors.accountNumber}
              autoComplete="off"
            />
          </EmbedField>
          {/* F23: the account type is locked to business checking, so the
              select is REPLACED by a read-only line, not disabled — a disabled
              control advertises a choice the customer cannot make. The capture
              sends account_type "checking" and the server refuses anything
              else, so this line is the form's only account-type surface. */}
          <div style={{ marginBottom: t.spacing.md }}>
            <p style={{ margin: 0, fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>
              Account type: Business checking
            </p>
          </div>
        </>
      )}

      {/* §3.8: the terms block and its acceptance control sit BETWEEN the
          fields and the submit button. */}
      <PaymentTermsGate
        documents={termsDocuments}
        loadError={termsError}
        accepted={accepted}
        onAcceptedChange={(next) => {
          setAccepted(next);
          if (next) setAcceptanceError(null);
        }}
        acceptanceError={acceptanceError}
      />

      <div style={{ display: "flex", gap: t.spacing.md, marginTop: t.spacing.lg }}>
        {onBack && (
          <EmbedButton type="button" variant="secondary" onClick={onBack}>
            Back
          </EmbedButton>
        )}
        <EmbedButton type="submit" variant="primary" disabled={!canSubmit} loading={busy}>
          {busy ? "Saving..." : "Continue"}
        </EmbedButton>
      </div>
    </form>
  );
}
